// =============================
// Email: info@xx.com
// www.xx.com/templates
// =============================

import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { LocalStoreManager } from './local-store-manager.service';
import { OidcHelperService } from './oidc-helper.service';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-keys';
import { JwtHelper } from './jwt-helper';
import { Utilities } from './utilities';
import { AccessToken, LoginResponse,MfaRequiredResponse} from '../models/login-response.model';
import { User } from '../models/user.model';
import { PermissionValues } from '../models/permission.model';
import { AlertService, AlertDialog, DialogType, AlertCommand, MessageSeverity } from '../services/alert.service';
import Swal from 'sweetalert2';

@Injectable()
export class AuthService {
  public get loginUrl() { return this.configurations.loginUrl; }
  public get homeUrl() { return this.configurations.homeUrl; }

  public loginRedirectUrl: string;
  public logoutRedirectUrl: string;

  public reLoginDelegate: () => void;
  private sessionWarningShown = false;


  private previousIsLoggedInCheck = false;
  private loginStatus = new Subject<boolean>();
  private tokenCheckInterval: any;
  constructor(
    private router: Router, private alertService: AlertService,
    private oidcHelperService: OidcHelperService,
    private configurations: ConfigurationService,
    private localStorage: LocalStoreManager) {

    this.initializeLoginStatus();
  }

  private initializeLoginStatus() {
    this.localStorage.getInitEvent().subscribe(() => {
      this.reevaluateLoginStatus();
    });
  }

  get isSessionExpiringSoon(): boolean {
    const expiry = this.accessTokenExpiryDate;
    if (!expiry) return false;

    const now = new Date();
    const warningMs = 15 * 60 * 1000; // 5 minutes before expiry
    return now.getTime() + warningMs >= expiry.getTime();
  }


  // ✅ Start periodic session check
  public startTokenExpiryMonitor(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    this.tokenCheckInterval = setInterval(() => {

      // ⚠️ Show popup BEFORE expiry
      if (this.isSessionExpiringSoon && !this.sessionWarningShown) {
        this.sessionWarningShown = true;
        this.showSessionExtendPopup();
        return;
      }

      // ❌ Hard expiry (no popup anymore)
      if (this.isSessionExpired) {
        this.forceLogout();
      }

    }, 60 * 1000); // check every 1 minute
  }

  private showSessionExtendPopup(): void {
    Swal.fire({
      title: 'Session Expiring',
      text: 'Your session will expire in a few minutes. Do you want to extend it?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Extend Session',
      cancelButtonText: 'Logout',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        await this.extendSessionAsync();
      }
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) {
        this.forceLogout();
      }
    });
  }

  private extendSessionAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.refreshLogin().subscribe({
        next: () => {
          this.sessionWarningShown = false;
          resolve();
        },
        error: () => {
          this.forceLogout();
          reject();
        }
      });
    });
  }

  private forceLogout(): void {
    if (!this.isLoggedIn) {
      return; // 🔒 already logged out
    }

    this.stopTokenExpiryMonitor();
    this.sessionWarningShown = false;

    this.logout();

    // 🔥 prevent multiple relogin triggers
    if (this.reLoginDelegate) {
      this.reLoginDelegate();
      this.reLoginDelegate = null;
    } else {
      this.redirectForLogin();
    }
  }



  private stopTokenExpiryMonitor(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }


  gotoPage(page: string, preserveParams = true) {

    const navigationExtras: NavigationExtras = {
      queryParamsHandling: preserveParams ? 'merge' : '', preserveFragment: preserveParams
    };

    this.router.navigate([page], navigationExtras);
  }

  gotoHomePage() {
    this.router.navigate([this.homeUrl]);
  }

  redirectLoginUser() {
    const redirect = this.loginRedirectUrl && this.loginRedirectUrl !== '/' && this.loginRedirectUrl !== ConfigurationService.defaultHomeUrl ? this.loginRedirectUrl : this.homeUrl;
    this.loginRedirectUrl = null;

    const urlParamsAndFragment = Utilities.splitInTwo(redirect, '#');
    const urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, '?');

    const navigationExtras: NavigationExtras = {
      fragment: urlParamsAndFragment.secondPart,
      queryParams: Utilities.getQueryParamsFromString(urlAndParams.secondPart),
      queryParamsHandling: 'merge'
    };

    this.router.navigate([urlAndParams.firstPart], navigationExtras);
  }

  redirectLogoutUser() {
    const redirect = this.logoutRedirectUrl ? this.logoutRedirectUrl : this.loginUrl;
    this.logoutRedirectUrl = null;

    this.router.navigate([redirect]);
  }

  redirectForLogin() {
    this.loginRedirectUrl = this.router.url;
    this.router.navigate([this.loginUrl]);
  }

  reLogin() {
    if (this.reLoginDelegate) {
      this.reLoginDelegate();
    } else {
      this.redirectForLogin();
    }
  }

  refreshLogin() {
    return this.oidcHelperService.refreshLogin()
      .pipe(map(resp => this.processLoginResponse(resp, this.rememberMe)));
  }

  loginWithPassword(userName: string, password: string, rememberMe?: boolean): Observable<User> {

    if (this.isLoggedIn) {
      this.logout();
    }

    return this.oidcHelperService.loginWithPassword(userName, password)
      .pipe(
        map(resp => this.processLoginResponse(resp, rememberMe))
      );
  }

  verifyMfa(mfaToken: string, code: string, rememberMe?: boolean, rememberDevice?: boolean): Observable<User> {
    return this.oidcHelperService.verifyMfa(mfaToken, code, rememberDevice)
      .pipe(map(resp => this.processLoginResponse(resp, rememberMe)));
  }

  // ---------------- Impersonation / proxy login ----------------

  // True while the current session is an admin acting as another user.
  get isImpersonating(): boolean {
    return this.localStorage.getDataObject<any>(DBkeys.IMPERSONATION_ADMIN) != null;
  }

  // The real admin behind an impersonated session (for the "return to admin" banner).
  get impersonatorUser(): User | null {
    const bundle = this.localStorage.getDataObject<any>(DBkeys.IMPERSONATION_ADMIN);
    return bundle ? bundle.user : null;
  }

  // Start impersonating the target user. The backend grant verifies the caller is an admin
  // and the target is a non-admin; on success the session becomes the target user.
  beginImpersonation(targetUserId: string): Observable<User> {
    if (this.isImpersonating) {
      throw new Error('Already impersonating another user. Return to admin first.');
    }

    const adminToken = this.accessToken;

    return this.oidcHelperService.impersonate(targetUserId, adminToken)
      .pipe(map(resp => {
        // Capture the admin session BEFORE processLoginResponse overwrites storage.
        const adminBundle = {
          accessToken: this.accessToken,
          refreshToken: this.refreshToken,
          expiresIn: this.accessTokenExpiryDate,
          permissions: this.userPermissions,
          user: this.currentUser,
          rememberMe: this.rememberMe
        };

        const user = this.processLoginResponse(resp, this.rememberMe);

        // Persist the admin bundle so "return to admin" can restore it across reloads.
        this.localStorage.savePermanentData(adminBundle, DBkeys.IMPERSONATION_ADMIN);

        return user;
      }));
  }

  // Restore the real admin session that was stashed when impersonation started.
  stopImpersonation(): boolean {
    const bundle = this.localStorage.getDataObject<any>(DBkeys.IMPERSONATION_ADMIN);
    if (!bundle) {
      return false;
    }

    this.saveUserDetails(
      bundle.user,
      bundle.permissions,
      bundle.accessToken,
      bundle.refreshToken,
      new Date(bundle.expiresIn),
      bundle.rememberMe === true);

    this.localStorage.deleteData(DBkeys.IMPERSONATION_ADMIN);

    this.reevaluateLoginStatus(bundle.user);
    this.startTokenExpiryMonitor();

    return true;
  }

  private processLoginResponse(response: LoginResponse, rememberMe?: boolean) {
    const accessToken = response.access_token;

    if (accessToken == null) {
      throw new Error('accessToken cannot be null');
    }

    rememberMe = rememberMe || this.rememberMe;

    const refreshToken = response.refresh_token || this.refreshToken;
    const expiresIn = response.expires_in;
    const tokenExpiryDate = new Date();
    tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + expiresIn);
    const accessTokenExpiry = tokenExpiryDate;
    const jwtHelper = new JwtHelper();
    const decodedAccessToken = jwtHelper.decodeToken(accessToken) as AccessToken;

    const mustChangePassword = decodedAccessToken.must_change_password == 'True';
    const permissions: PermissionValues[] = Array.isArray(decodedAccessToken.permission) ? decodedAccessToken.permission : [decodedAccessToken.permission];

    if (!this.isLoggedIn) {
      this.configurations.import(decodedAccessToken.configuration);
    }

    const user = new User(
      decodedAccessToken.sub,
      decodedAccessToken.name,
      decodedAccessToken.fullname,
      decodedAccessToken.email,
      decodedAccessToken.jobtitle,
      decodedAccessToken.phone_number,
      Array.isArray(decodedAccessToken.role) ? decodedAccessToken.role : [decodedAccessToken.role]);
    user.isEnabled = true;

    this.saveUserDetails(user, permissions, accessToken, refreshToken, accessTokenExpiry, rememberMe);

    this.reevaluateLoginStatus(user);

    this.startTokenExpiryMonitor();

    if (mustChangePassword) {
      this.router.navigate(
        ['/settings'],
        {
          fragment: 'profile',
          queryParams: { forcePasswordChange: true }
        }
      );

    }

    return user;
  }

  private saveUserDetails(user: User, permissions: PermissionValues[], accessToken: string, refreshToken: string, expiresIn: Date, rememberMe: boolean) {
    if (rememberMe) {
      this.localStorage.savePermanentData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.savePermanentData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.savePermanentData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
      this.localStorage.savePermanentData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
    } else {
      this.localStorage.saveSyncedSessionData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.saveSyncedSessionData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.saveSyncedSessionData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
      this.localStorage.saveSyncedSessionData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
    }

    this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
  }

  logout(): void {
    this.stopTokenExpiryMonitor();
    this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
    this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
    this.localStorage.deleteData(DBkeys.USER_PERMISSIONS);
    this.localStorage.deleteData(DBkeys.CURRENT_USER);
    this.localStorage.deleteData(DBkeys.IMPERSONATION_ADMIN);

    this.configurations.clearLocalChanges();

    this.reevaluateLoginStatus();
    this.alertService.showStickyMessage('Session Expired', 'Your Session has expired. Please log in again', MessageSeverity.info);
  }

  private reevaluateLoginStatus(currentUser?: User) {
    const user = currentUser || this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    const isLoggedIn = user != null;

    if (this.previousIsLoggedInCheck !== isLoggedIn) {
      setTimeout(() => {
        this.loginStatus.next(isLoggedIn);
      });
    }

    this.previousIsLoggedInCheck = isLoggedIn;
  }

  getLoginStatusEvent(): Observable<boolean> {
    return this.loginStatus.asObservable();
  }

  get currentUser(): User {

    const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    this.reevaluateLoginStatus(user);

    return user;
  }

  get userPermissions(): PermissionValues[] {
    return this.localStorage.getDataObject<PermissionValues[]>(DBkeys.USER_PERMISSIONS) || [];
  }

  get accessToken(): string {
    return this.oidcHelperService.accessToken;
  }

  get accessTokenExpiryDate(): Date {
    return this.oidcHelperService.accessTokenExpiryDate;
  }

  get refreshToken(): string {
    return this.oidcHelperService.refreshToken;
  }

  get isSessionExpired(): boolean {
    
    const expiry = this.accessTokenExpiryDate;
    if (!expiry) return true;

    const now = new Date();
    const bufferMs = 60 * 1000; // 1 minute
    return now.getTime() + bufferMs >= expiry.getTime();
  }

  get isLoggedIn(): boolean {
    return this.currentUser != null;
  }

  get rememberMe(): boolean {
    return this.localStorage.getDataObject<boolean>(DBkeys.REMEMBER_ME) === true;
  }
}
